import { Node, mergeAttributes } from "@tiptap/core";
import { Plugin } from "prosemirror-state";

export const TEMPLATE_VARIABLE_REGEX = /\{\{\s*([^{}\s][^{}]*?)\s*\}\}/g;

export const TemplateVariable = Node.create({
	name: "templateVariable",
	group: "inline",
	inline: true,
	atom: true,
	selectable: true,
	draggable: false,

	addAttributes() {
		return {
			name: {
				default: "",
				parseHTML: (el) => el.getAttribute("data-name") ?? "",
				renderHTML: (attrs) => ({ "data-name": attrs.name }),
			},
		};
	},

	parseHTML() {
		return [{ tag: "span[data-template-variable]" }];
	},

	renderHTML({ HTMLAttributes, node }) {
		return [
			"span",
			mergeAttributes(HTMLAttributes, {
				"data-template-variable": "",
				class: "template-variable",
				contenteditable: "false",
			}),
			`{{${node.attrs.name}}}`,
		];
	},

	renderText({ node }) {
		return `{{${node.attrs.name}}}`;
	},
});

export const DefaultTemplateVariable = Node.create({
	name: "defaultTemplateVariable",
	group: "block",
	atom: true,
	selectable: false,
	draggable: false,

	addAttributes() {
		return {
			name: {
				default: "",
				parseHTML: (el) => el.getAttribute("data-name") ?? "",
				renderHTML: (attrs) => ({ "data-name": attrs.name }),
			},
		};
	},

	parseHTML() {
		return [{ tag: "div[data-default-template-variable]" }];
	},

	renderHTML({ HTMLAttributes, node }) {
		return [
			"div",
			mergeAttributes(HTMLAttributes, {
				"data-default-template-variable": "",
				style: "display:none",
				contenteditable: "false",
			}),
			node.attrs.name,
		];
	},

	renderText({ node }) {
		return `{{${node.attrs.name}}}`;
	},

	addProseMirrorPlugins() {
		return [
			new Plugin({
				filterTransaction(tr, state) {
					if (!tr.docChanged) return true;

					const countsBefore = new Map<string, number>();
					state.doc.descendants((node) => {
						if (node.type.name === "defaultTemplateVariable") {
							const name = node.attrs.name as string;
							countsBefore.set(name, (countsBefore.get(name) ?? 0) + 1);
						}
					});

					if (countsBefore.size === 0) return true;

					const countsAfter = new Map<string, number>();
					tr.doc.descendants((node) => {
						if (node.type.name === "defaultTemplateVariable") {
							const name = node.attrs.name as string;
							countsAfter.set(name, (countsAfter.get(name) ?? 0) + 1);
						}
					});

					for (const [name, count] of countsBefore) {
						if ((countsAfter.get(name) ?? 0) < count) return false;
					}

					return true;
				},
			}),
		];
	},
});

export const PRIMARY_COLOR_VARIABLE_NAME = "PrimaryColor";
export const PRIMARY_COLOR_SENTINEL = "#000001";

function parseColorToRgb(color: string): string | null {
	const c = color.trim().toLowerCase();

	const hexMatch = c.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/);
	if (hexMatch) {
		let hex = hexMatch[1];
		if (hex.length === 3) {
			hex = hex
				.split("")
				.map((ch) => ch + ch)
				.join("");
		}
		const r = parseInt(hex.slice(0, 2), 16);
		const g = parseInt(hex.slice(2, 4), 16);
		const b = parseInt(hex.slice(4, 6), 16);
		return `${r},${g},${b}`;
	}

	const rgbMatch = c.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
	if (rgbMatch) {
		return `${parseInt(rgbMatch[1], 10)},${parseInt(rgbMatch[2], 10)},${parseInt(rgbMatch[3], 10)}`;
	}

	return null;
}

function replaceInStyleAttributes(html: string, replacer: (styleContent: string) => string): string {
	return html.replace(/style="([^"]*)"/gi, (_, styleContent) => `style="${replacer(styleContent)}"`);
}

function parseVerticalBorderSpacing(styleAttr: string | null): number {
	if (!styleAttr) return 0;
	const match = styleAttr.match(/border-spacing\s*:\s*([^;]+)/i);
	if (!match) return 0;
	const parts = match[1].trim().split(/\s+/);
	const vertical = parts.length > 1 ? parts[1] : parts[0];
	const px = parseFloat(vertical);
	return Number.isNaN(px) ? 0 : px;
}

function convertTablesToParagraphs(html: string): string {
	const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");

	Array.from(doc.querySelectorAll("table"))
		.reverse()
		.forEach((table) => {
			const vertical = parseVerticalBorderSpacing(table.getAttribute("style"));
			const fragment = doc.createDocumentFragment();

			table.querySelectorAll("tr").forEach((row) => {
				const paragraph = doc.createElement("p");
				const cells = Array.from(row.querySelectorAll("th, td"));

				const styles: string[] = [];
				if (vertical > 0) styles.push(`margin: ${vertical / 2}px 0`);
				const align = cells.find((c) => c.getAttribute("align"))?.getAttribute("align");
				if (align && align.toLowerCase() !== "left") styles.push(`text-align: ${align.toLowerCase()}`);
				if (styles.length > 0) paragraph.setAttribute("style", `${styles.join("; ")};`);

				cells.forEach((cell, index) => {
					if (index > 0) paragraph.appendChild(doc.createTextNode(" "));
					while (cell.firstChild) paragraph.appendChild(cell.firstChild);
				});

				fragment.appendChild(paragraph);
			});

			table.replaceWith(fragment);
		});

	return doc.body.innerHTML;
}

export function htmlToEditorContent(html: string, defaultVariables: string[] = [], primaryColor?: string): string {
	const effectiveColor = primaryColor ?? PRIMARY_COLOR_SENTINEL;

	let processed = replaceInStyleAttributes(html, (styleContent) =>
		styleContent.replace(new RegExp(`\\{\\{\\s*${PRIMARY_COLOR_VARIABLE_NAME}\\s*\\}\\}`, "g"), effectiveColor),
	);

	if (/<table[\s>]/i.test(processed)) {
		processed = convertTablesToParagraphs(processed);
	}

	return processed.replace(/<[^>]*>|\{\{\s*([^{}\s][^{}]*?)\s*\}\}/g, (match, name) => {
		if (name === undefined) return match;
		const trimmedName = name.trim();
		if (defaultVariables.includes(trimmedName)) {
			return `<div data-default-template-variable data-name="${trimmedName}"></div>`;
		}
		return `<span data-template-variable data-name="${trimmedName}"></span>`;
	});
}

export function editorContentToHtml(html: string, primaryColor?: string): string {
	const withInlineVars = html.replace(
		/<span\b(?=[^>]*\bdata-template-variable\b)[^>]*\bdata-name="([^"]+)"[^>]*>[\s\S]*?<\/span>/g,
		(_, name) => `{{${name}}}`,
	);

	const withDefaultVars = withInlineVars.replace(
		/<div\b(?=[^>]*\bdata-default-template-variable\b)[^>]*\bdata-name="([^"]+)"[^>]*>[\s\S]*?<\/div>/g,
		(_, name) => `{{${name}}}`,
	);

	let result = withDefaultVars.replace(/(?:\s*<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>\s*)+$/i, "");

	const effectiveColor = primaryColor ?? PRIMARY_COLOR_SENTINEL;
	const targetRgb = parseColorToRgb(effectiveColor);

	if (targetRgb) {
		result = replaceInStyleAttributes(result, (styleContent) =>
			styleContent.replace(/color\s*:\s*([^;]+)/gi, (full, value) => {
				const valueRgb = parseColorToRgb(value);
				return valueRgb && valueRgb === targetRgb ? `color: {{${PRIMARY_COLOR_VARIABLE_NAME}}}` : full;
			}),
		);
	}

	return result;
}
